import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { type Dayjs } from '@/lib/dayjs'
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/shadcn/card'
import { Button } from '@/components/shadcn/button'
import { Label } from '@/components/shadcn/label'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/shadcn/select'
import { StatusPayoutInterval } from '@/api/model'
import type {
  StatusSystemServiceModuleModel,
  UpdateStatusSystemServiceModuleModel,
} from '@/api/model'
import { WalletTypeSelect } from '@/components/common/WalletTypeSelect'
import { StatusFirstExecutionPicker } from './StatusFirstExecutionPicker'

const INTERVAL_OPTIONS: StatusPayoutInterval[] = [
  StatusPayoutInterval.Daily,
  StatusPayoutInterval.Weekly,
  StatusPayoutInterval.Monthly,
]

interface Props {
  module: StatusSystemServiceModuleModel
  canEdit: boolean
  isSaving?: boolean
  onSave: (data: UpdateStatusSystemServiceModuleModel) => void
}

/**
 * Settings of the status module (schedule + wallets). Local form state is seeded
 * from `module` via an effect (re-sync after every refetch). PATCH is a
 * full replace (§3): the save sends the shared module fields **unchanged** —
 * they're never edited here, they belong to the metadata editor. `firstExecution`
 * is normalized to a UTC-midnight ISO string (day-boundary gotcha). `nextExecution`
 * is server-computed and read-only.
 */
export function StatusModuleSettingsCard({ module, canEdit, isSaving, onSave }: Props) {
  const { t } = useTranslation(['service-modules-status', 'common'])

  const [statusPointsWalletTypeId, setStatusPointsWalletTypeId] = useState<string | null>(null)
  const [payoutWalletTypeId, setPayoutWalletTypeId] = useState<string | null>(null)
  const [payoutInterval, setPayoutInterval] = useState<StatusPayoutInterval>(StatusPayoutInterval.Monthly)
  const [firstExecution, setFirstExecution] = useState<Dayjs | null>(null)

  useEffect(() => {
    setStatusPointsWalletTypeId(module.statusPointsWalletTypeId ?? null)
    setPayoutWalletTypeId(module.payoutWalletTypeId ?? null)
    setPayoutInterval(module.payoutInterval)
    setFirstExecution(module.firstExecution ? dayjs(module.firstExecution) : null)
  }, [module])

  const handleSave = () => {
    onSave({
      // Pass the shared fields through unchanged (full-replace PATCH).
      name: module.name,
      iconName: module.iconName,
      colorHex: module.colorHex,
      sendNotification: module.sendNotification,
      isVisible: module.isVisible,
      isCompanyWideAssigned: module.isCompanyWideAssigned,
      assignedGroupIds: module.assignedGroupIds,
      // Status-specific fields from the local state.
      statusPointsWalletTypeId,
      payoutWalletTypeId,
      payoutInterval,
      firstExecution: firstExecution
        ? dayjs.utc(firstExecution.format('YYYY-MM-DD')).toISOString()
        : null,
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Schedule */}
          <div className="flex flex-col gap-4">
            <p className="text-sm font-medium text-muted-foreground">{t('settings.scheduleSectionTitle')}</p>

            <div className="flex flex-col gap-1.5">
              <Label>{t('settings.intervalLabel')}</Label>
              <Select
                value={payoutInterval}
                onValueChange={(v) => setPayoutInterval(v as StatusPayoutInterval)}
                disabled={!canEdit}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVAL_OPTIONS.map((interval) => (
                    <SelectItem key={interval} value={interval}>
                      {t(`settings.interval.${interval}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('settings.firstExecutionLabel')}</Label>
              <StatusFirstExecutionPicker value={firstExecution} onChange={setFirstExecution} disabled={!canEdit} />
              {!firstExecution && (
                <p className="text-xs text-muted-foreground">{t('settings.payoutInactiveHint')}</p>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              {t('settings.nextExecutionLabel')}:{' '}
              <span className="font-medium text-foreground">
                {module.nextExecution ? dayjs(module.nextExecution).format('DD.MM.YYYY') : '—'}
              </span>
            </p>
          </div>

          {/* Wallets */}
          <div className="flex flex-col gap-4">
            <p className="text-sm font-medium text-muted-foreground">{t('settings.walletsSectionTitle')}</p>

            <div className="flex flex-col gap-1.5">
              <Label>{t('settings.statusPointsWalletLabel')}</Label>
              <WalletTypeSelect
                value={statusPointsWalletTypeId}
                onChange={setStatusPointsWalletTypeId}
                disabled={!canEdit}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('settings.payoutWalletLabel')}</Label>
              <WalletTypeSelect value={payoutWalletTypeId} onChange={setPayoutWalletTypeId} disabled={!canEdit} />
            </div>
          </div>
        </div>

        {canEdit && (
          <div>
            <Button onClick={handleSave} disabled={isSaving}>
              {t('buttons.save', { ns: 'common' })}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
